/**
 * DraftManager - Local draft storage using localStorage
 *
 * Key features:
 * - Auto-save every 30 seconds
 * - Save on visibility change (tab switch)
 * - Recovery modal on page reload
 * - Clear drafts on server acknowledgment (not on localStorage write)
 *
 * Implementation: Task 2.0
 *
 * Usage:
 *   const draftManager = new DraftManager(websiteId, pageId, {
 *     adminId: CONFIG.adminId,
 *     onDraftRecoverable: (draft) => { showRecoveryModal(draft) }
 *   })
 *
 *   draftManager.recordChange(blockId, { headline: 'New text' }, { headline: 'Old text' })
 *   draftManager.startAutoSave()
 *
 *   // When SaveQueue succeeds
 *   draftManager.clearBlockFromDraft(blockId)
 */

import { trackEditorEvent, EditorEvents } from './editor_analytics.js'
import { createLogger } from './logger.js'

// Phase 5: Shared logger with debug gating
const logger = createLogger('DraftManager')

// Storage key prefix for all drafts
const STORAGE_KEY_PREFIX = 'madely_editor_draft_'

// Default configuration
const DEFAULT_OPTIONS = {
  autoSaveInterval: 30000, // 30 seconds
  maxDraftAgeMs: 7 * 24 * 60 * 60 * 1000, // Phase 10: 7 days max draft age
  adminId: null, // Will be set from CONFIG
  onDraftRecoverable: null, // Callback when recoverable draft found
  onDraftSaved: null, // Callback when draft saved to localStorage
  onDraftCleared: null, // Callback when draft cleared
  enableBeforeUnload: true // Phase 10: Warn user before leaving with pending changes
}

/**
 * Draft payload structure stored in localStorage
 * @typedef {Object} DraftPayload
 * @property {string} websiteId - Website UUID
 * @property {string} pageId - Page UUID
 * @property {string} editorId - Admin who created this draft
 * @property {number} createdAt - Timestamp when draft was first created
 * @property {number} updatedAt - Timestamp of last update
 * @property {Object<string, BlockDraft>} blocks - Map of blockId to draft data
 */

/**
 * Individual block draft data
 * @typedef {Object} BlockDraft
 * @property {Object} content - Current edited content
 * @property {Object} originalContent - Content before editing (for diff/restore)
 * @property {number} updatedAt - Timestamp of last change to this block
 */

export class DraftManager {
  /**
   * @param {string} websiteId - Website UUID
   * @param {string} pageId - Page UUID
   * @param {Object} options - Configuration options
   * @param {string} options.adminId - Current admin ID (from CONFIG.adminId)
   * @param {Function} options.onDraftRecoverable - Callback when recoverable draft found
   * @param {number} options.autoSaveInterval - Auto-save interval in ms (default 30000)
   */
  constructor(websiteId, pageId, options = {}) {
    this.websiteId = websiteId
    this.pageId = pageId
    this.options = { ...DEFAULT_OPTIONS, ...options }

    // Storage key for this specific page
    this.storageKey = `${STORAGE_KEY_PREFIX}${websiteId}_${pageId}`

    // In-memory pending changes (not yet written to localStorage)
    // Map<blockId, BlockDraft>
    this.pendingChanges = new Map()

    // Auto-save state
    this.autoSaveIntervalId = null
    this.visibilityChangeHandler = null

    // Track if we've already checked for recoverable draft this session
    this.recoveryCheckDone = false

    // Phase 10: Set up beforeunload warning for pending changes
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this)
    if (this.options.enableBeforeUnload) {
      window.addEventListener('beforeunload', this.handleBeforeUnload)
    }

    // Phase 10: Clean up expired drafts on initialization
    this.cleanupExpiredDrafts()

    logger.log('Initialized:', {
      websiteId,
      pageId,
      storageKey: this.storageKey,
      adminId: this.options.adminId
    })
  }

  /**
   * Phase 10: Handle beforeunload event to warn about pending changes
   * @private
   */
  handleBeforeUnload(event) {
    if (this.hasPendingChanges()) {
      // Save pending changes before potentially leaving
      this.saveDraft()
      // Show browser warning
      event.preventDefault()
      event.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
      return event.returnValue
    }
  }

  /**
   * Phase 10: Clean up expired drafts from localStorage
   * Removes drafts older than maxDraftAgeMs
   * Phase 10 FIX: Collect keys first to avoid index mutation during iteration
   * @private
   */
  cleanupExpiredDrafts() {
    const now = Date.now()
    const maxAge = this.options.maxDraftAgeMs
    let cleanedCount = 0

    // Phase 10 FIX: Collect all keys first to avoid index shifting during removal
    const keysToCheck = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        keysToCheck.push(key)
      }
    }

    // Now safely iterate over the snapshot of keys
    for (const key of keysToCheck) {
      try {
        const stored = localStorage.getItem(key)
        if (stored) {
          const draft = JSON.parse(stored)
          // Check if draft is expired
          if (draft.updatedAt && (now - draft.updatedAt) > maxAge) {
            localStorage.removeItem(key)
            cleanedCount++
            logger.log('Removed expired draft:', key)
          }
        }
      } catch (e) {
        // Invalid JSON, remove it
        localStorage.removeItem(key)
        cleanedCount++
      }
    }

    if (cleanedCount > 0) {
      logger.log('Cleaned up', cleanedCount, 'expired drafts')
    }
  }

  /**
   * Record a change to a block
   * Changes are held in memory until saveDraft() is called
   *
   * @param {string} blockId - Block ID
   * @param {Object} content - New content
   * @param {Object} originalContent - Original content before editing
   */
  recordChange(blockId, content, originalContent = null) {
    const now = Date.now()
    const existing = this.pendingChanges.get(blockId)

    if (existing) {
      // Update existing pending change
      existing.content = { ...existing.content, ...content }
      existing.updatedAt = now
    } else {
      // Create new pending change
      this.pendingChanges.set(blockId, {
        content,
        originalContent: originalContent || {},
        updatedAt: now
      })
    }

    logger.log('Recorded change for block:', blockId, 'pending:', this.pendingChanges.size)
  }

  /**
   * Save all pending changes to localStorage
   * Uses plain JSON.stringify (no compression per Gate 0.2)
   *
   * @returns {boolean} True if saved successfully
   */
  saveDraft() {
    if (this.pendingChanges.size === 0) {
      return true // Nothing to save
    }

    const now = Date.now()

    // Load existing draft or create new one
    const draft = this.getExistingDraft() || {
      websiteId: this.websiteId,
      pageId: this.pageId,
      editorId: this.options.adminId,
      createdAt: now,
      updatedAt: now,
      blocks: {}
    }

    // Merge pending changes into draft
    for (const [blockId, blockDraft] of this.pendingChanges.entries()) {
      draft.blocks[blockId] = {
        ...draft.blocks[blockId],
        ...blockDraft
      }
    }

    draft.updatedAt = now
    draft.editorId = this.options.adminId // Update editor ID in case of switch

    // Save to localStorage
    try {
      const serialized = JSON.stringify(draft)
      localStorage.setItem(this.storageKey, serialized)

      logger.log('Draft saved:', {
        blockCount: Object.keys(draft.blocks).length,
        size: serialized.length
      })

      // Track analytics
      trackEditorEvent(EditorEvents.DRAFT_SAVED, {
        websiteId: this.websiteId,
        pageId: this.pageId,
        blockCount: Object.keys(draft.blocks).length,
        sizeBytes: serialized.length
      })

      // Notify callback
      if (typeof this.options.onDraftSaved === 'function') {
        this.options.onDraftSaved(draft)
      }

      // Clear pending changes (they're now in localStorage)
      this.pendingChanges.clear()

      return true
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        logger.warn(' QuotaExceededError - attempting cleanup')
        return this.handleQuotaExceeded(draft)
      }
      console.error('[DraftManager] Failed to save draft:', error)
      return false
    }
  }

  /**
   * Handle QuotaExceededError by cleaning up old drafts
   * @private
   */
  handleQuotaExceeded(draft) {
    // Find and delete other drafts with same prefix
    const keysToDelete = []

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STORAGE_KEY_PREFIX) && key !== this.storageKey) {
        keysToDelete.push(key)
      }
    }

    // Sort by age (oldest first) if we have timestamps
    // For now, just delete all other drafts
    logger.log('Cleaning up', keysToDelete.length, 'old drafts')

    for (const key of keysToDelete) {
      try {
        localStorage.removeItem(key)
      } catch (e) {
        // Ignore errors during cleanup
      }
    }

    // Retry save once
    try {
      const serialized = JSON.stringify(draft)
      localStorage.setItem(this.storageKey, serialized)
      this.pendingChanges.clear()
      logger.log('Draft saved after cleanup')
      return true
    } catch (error) {
      console.error('[DraftManager] Still cannot save after cleanup:', error)
      return false
    }
  }

  /**
   * Start auto-save with interval and visibility change handling
   *
   * @param {number} intervalMs - Auto-save interval in ms (default from options)
   */
  startAutoSave(intervalMs = null) {
    const interval = intervalMs || this.options.autoSaveInterval

    // Clear any existing auto-save
    this.stopAutoSave()

    // Set up interval auto-save (every 30 seconds by default)
    this.autoSaveIntervalId = setInterval(() => {
      if (this.pendingChanges.size > 0) {
        logger.log('Auto-save triggered')
        this.saveDraft()
      }
    }, interval)

    // Set up visibility change handler (save when tab is hidden)
    this.visibilityChangeHandler = () => {
      if (document.hidden && this.pendingChanges.size > 0) {
        logger.log('Visibility change save triggered')
        this.saveDraft()
      }
    }
    document.addEventListener('visibilitychange', this.visibilityChangeHandler)

    logger.log('Auto-save started with interval:', interval)
  }

  /**
   * Stop auto-save
   */
  stopAutoSave() {
    if (this.autoSaveIntervalId) {
      clearInterval(this.autoSaveIntervalId)
      this.autoSaveIntervalId = null
    }

    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler)
      this.visibilityChangeHandler = null
    }
  }

  /**
   * Check if there's a recoverable draft in localStorage
   *
   * @returns {boolean}
   */
  hasRecoverableDraft() {
    const draft = this.getExistingDraft()

    if (!draft || !draft.blocks) {
      return false
    }

    // Check if draft has any blocks
    return Object.keys(draft.blocks).length > 0
  }

  /**
   * Get existing draft from localStorage
   * Phase 10: Enhanced validation to handle corrupted drafts
   *
   * @returns {DraftPayload|null}
   */
  getExistingDraft() {
    try {
      const stored = localStorage.getItem(this.storageKey)
      if (!stored) {
        return null
      }

      const draft = JSON.parse(stored)

      // Phase 10: Comprehensive draft validation
      if (!this.validateDraftStructure(draft)) {
        logger.warn('Invalid draft structure, removing corrupted draft')
        // Phase 10 FIX: Use removeStoredDraft() to avoid clearing pendingChanges
        this.removeStoredDraft()
        return null
      }

      // Phase 10: Check if draft is expired
      const now = Date.now()
      if (draft.updatedAt && (now - draft.updatedAt) > this.options.maxDraftAgeMs) {
        logger.warn('Draft expired, removing')
        // Phase 10 FIX: Use removeStoredDraft() to avoid clearing pendingChanges
        this.removeStoredDraft()
        return null
      }

      return draft
    } catch (error) {
      console.error('[DraftManager] Failed to parse draft:', error)
      // Remove corrupted draft
      try {
        localStorage.removeItem(this.storageKey)
      } catch (e) { /* ignore */ }
      return null
    }
  }

  /**
   * Phase 10: Validate draft structure is well-formed
   * @private
   * @param {Object} draft - Draft object to validate
   * @returns {boolean} True if valid
   */
  validateDraftStructure(draft) {
    // Basic type check
    if (!draft || typeof draft !== 'object') {
      return false
    }

    // Required fields
    if (!draft.websiteId || typeof draft.websiteId !== 'string') {
      return false
    }
    if (!draft.pageId || typeof draft.pageId !== 'string') {
      return false
    }

    // Blocks must be an object if present
    if (draft.blocks !== undefined && (typeof draft.blocks !== 'object' || draft.blocks === null)) {
      return false
    }

    // Timestamps must be numbers if present
    if (draft.createdAt !== undefined && typeof draft.createdAt !== 'number') {
      return false
    }
    if (draft.updatedAt !== undefined && typeof draft.updatedAt !== 'number') {
      return false
    }

    // Validate each block if blocks exist
    if (draft.blocks) {
      for (const [blockId, blockDraft] of Object.entries(draft.blocks)) {
        // Block ID should be a string
        if (typeof blockId !== 'string' || !blockId) {
          return false
        }
        // Block draft should be an object with content
        if (!blockDraft || typeof blockDraft !== 'object') {
          return false
        }
        // Content should exist
        if (blockDraft.content === undefined) {
          return false
        }
      }
    }

    return true
  }

  /**
   * Check for recoverable draft and notify callback
   * Call this on page load to show recovery modal if needed
   *
   * @returns {DraftPayload|null} The recoverable draft, if any
   */
  checkForRecoverableDraft() {
    if (this.recoveryCheckDone) {
      return null
    }

    this.recoveryCheckDone = true
    const draft = this.getExistingDraft()

    if (draft && Object.keys(draft.blocks || {}).length > 0) {
      logger.log('Recoverable draft found:', {
        blockCount: Object.keys(draft.blocks).length,
        updatedAt: draft.updatedAt,
        editorId: draft.editorId
      })

      // Notify callback
      if (typeof this.options.onDraftRecoverable === 'function') {
        this.options.onDraftRecoverable(draft)
      }

      return draft
    }

    return null
  }

  /**
   * Clear a specific block from the draft
   * Call this when SaveQueue reports successful server save
   *
   * @param {string} blockId - Block ID to clear
   */
  clearBlockFromDraft(blockId) {
    // Clear from pending changes
    this.pendingChanges.delete(blockId)

    // Clear from stored draft
    const draft = this.getExistingDraft()
    if (draft && draft.blocks && draft.blocks[blockId]) {
      delete draft.blocks[blockId]

      // If no more blocks, remove the draft entirely
      if (Object.keys(draft.blocks).length === 0) {
        this.clearDraft()
      } else {
        // Update the draft
        draft.updatedAt = Date.now()
        try {
          localStorage.setItem(this.storageKey, JSON.stringify(draft))
        } catch (e) {
          // Ignore errors
        }
      }

      logger.log('Cleared block from draft:', blockId)
    }
  }

  /**
   * Phase 10 FIX: Remove stored draft from localStorage without clearing pendingChanges
   * Use this for cleanup of corrupted/expired drafts where we want to preserve in-memory edits
   * @private
   */
  removeStoredDraft() {
    try {
      localStorage.removeItem(this.storageKey)
      logger.log('Stored draft removed (pendingChanges preserved)')
    } catch (error) {
      console.error('[DraftManager] Failed to remove stored draft:', error)
    }
  }

  /**
   * Clear the entire draft (user-initiated discard)
   * This clears both localStorage AND pendingChanges - use only when user explicitly discards
   */
  clearDraft() {
    try {
      localStorage.removeItem(this.storageKey)
      this.pendingChanges.clear()

      logger.log('Draft cleared')

      // Track analytics
      trackEditorEvent(EditorEvents.DRAFT_DISCARDED, {
        websiteId: this.websiteId,
        pageId: this.pageId
      })

      // Notify callback
      if (typeof this.options.onDraftCleared === 'function') {
        this.options.onDraftCleared()
      }
    } catch (error) {
      console.error('[DraftManager] Failed to clear draft:', error)
    }
  }

  /**
   * Apply recovered draft to the page
   * Call this when user clicks "Recover Changes"
   *
   * @param {DraftPayload} draft - The draft to recover
   * @param {Function} applyBlockContent - Callback to apply content to a block: (blockId, content) => void
   * @returns {number} Number of blocks recovered
   */
  recoverDraft(draft, applyBlockContent) {
    if (!draft || !draft.blocks) {
      return 0
    }

    let recoveredCount = 0

    for (const [blockId, blockDraft] of Object.entries(draft.blocks)) {
      if (typeof applyBlockContent === 'function') {
        try {
          applyBlockContent(blockId, blockDraft.content)
          recoveredCount++
        } catch (error) {
          console.error('[DraftManager] Failed to recover block:', blockId, error)
        }
      }
    }

    logger.log('Recovered', recoveredCount, 'blocks')

    // Track analytics
    trackEditorEvent(EditorEvents.DRAFT_RECOVERED, {
      websiteId: this.websiteId,
      pageId: this.pageId,
      blockCount: recoveredCount
    })

    return recoveredCount
  }

  /**
   * Check if there are pending changes not yet saved to localStorage
   *
   * @returns {boolean}
   */
  hasPendingChanges() {
    return this.pendingChanges.size > 0
  }

  /**
   * Get relative time string for draft timestamp
   *
   * @param {number} timestamp - Unix timestamp
   * @returns {string} Human-readable relative time
   */
  static getRelativeTime(timestamp) {
    const now = Date.now()
    const diff = now - timestamp

    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) {
      return days === 1 ? '1 day ago' : `${days} days ago`
    }
    if (hours > 0) {
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`
    }
    if (minutes > 0) {
      return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`
    }
    return 'just now'
  }

  /**
   * Subscribe to SaveQueue for automatic draft clearing
   * Call this after creating both DraftManager and SaveQueue
   *
   * @param {SaveQueue} saveQueue - The SaveQueue instance
   */
  subscribeToSaveQueue(saveQueue) {
    if (!saveQueue) {
      logger.warn(' Cannot subscribe - no SaveQueue provided')
      return
    }

    // Use subscribeStatus() method to add listener (not options.onStatusChange which is only read at construction)
    saveQueue.subscribeStatus((status, item, _extra) => {
      // Clear block from draft on successful save
      if (status === 'completed' && item?.blockId) {
        logger.log('Clearing saved block from draft:', item.blockId)
        this.clearBlockFromDraft(item.blockId)
      }
    })

    logger.log('Subscribed to SaveQueue')
  }

  /**
   * Destroy the draft manager and clean up
   */
  destroy() {
    this.stopAutoSave()
    this.pendingChanges.clear()

    // Phase 10: Remove beforeunload listener
    if (this.options.enableBeforeUnload) {
      window.removeEventListener('beforeunload', this.handleBeforeUnload)
    }

    logger.log('Destroyed')
  }
}
